// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO (notifications reskin 2026-09): Sounds on the phone. Volume and mute-all in one card,
// the three message sounds in another, and the rest (calls and voice, your own sounds,
// per-sound volume) behind disclosure rows so the page stays short.

import {CUSTOM_SOUND_MAX_SIZE_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import {type SoundType, SoundType as SoundTypes} from '@app/features/notification/utils/SoundUtils';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {
	CaretRightIcon,
	ChatCircleDotsIcon,
	ChatCircleIcon,
	HashIcon,
	HeadsetIcon,
	PlayIcon,
	SlidersHorizontalIcon,
	SpeakerHighIcon,
	SpeakerSlashIcon,
	TrashIcon,
	UploadIcon,
} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {formatRoundedPercentage} from '@app/features/ui/utils/PercentageFormatting';
import {
	getSoundIcon,
	type Sounds,
	SoundVolumeOverrides,
} from '@app/features/user/components/modals/tabs/notifications_tab/NotificationsTabSounds';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/PhoneNotifications.module.css';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId, useState} from 'react';

const VOLUME_DESCRIPTOR = msg({
	message: 'Volume',
	comment: 'Phone Settings > Notifications > Sounds: accessible name of the master volume slider.',
});
const MUTE_ALL_SOUNDS_DESCRIPTOR = msg({
	message: 'Mute all sounds',
	comment: 'Phone Settings > Notifications > Sounds: switch that silences every notification sound.',
});
const KEEPS_YOUR_CHOICES_DESCRIPTOR = msg({
	message: 'Keeps your choices below',
	comment: 'Phone Settings > Notifications > Sounds: under "Mute all sounds"; the per-sound switches are kept.',
});
const MESSAGE_SOUNDS_DESCRIPTOR = msg({
	message: 'Message sounds',
	comment: 'Phone Settings > Notifications: label above the sounds for new messages.',
});
const SERVER_MESSAGES_DESCRIPTOR = msg({
	message: 'Server messages',
	comment: 'Phone Settings > Notifications > Message sounds: sound for a new message in a server channel.',
});
const DIRECT_MESSAGES_DESCRIPTOR = msg({
	message: 'Direct messages',
	comment: 'Phone Settings > Notifications > Message sounds: sound for a new direct message.',
});
const CHAT_YOURE_IN_DESCRIPTOR = msg({
	message: "Chat you're in",
	comment: 'Phone Settings > Notifications > Message sounds: sound for a new message in the chat that is open.',
});
const CALLS_AND_VOICE_DESCRIPTOR = msg({
	message: 'Calls and voice',
	comment: 'Phone Settings > Notifications > Message sounds: row that opens the call, voice and camera sounds.',
});
const YOUR_OWN_SOUNDS_DESCRIPTOR = msg({
	message: 'Your own sounds',
	comment: 'Phone Settings > Notifications > Message sounds: row that opens the list for uploading custom sounds.',
});
const UP_TO_SIZE_DESCRIPTOR = msg({
	message: 'Up to {size}',
	comment: 'Phone Settings > Notifications: largest custom sound file, e.g. "Up to 2 MB". Preserve {size}.',
});
const PICK_A_SOUND_DESCRIPTOR = msg({
	message: 'Replace any sound with your own audio file.',
	comment: 'Phone Settings > Notifications > Your own sounds: one line above the list of sounds.',
});
const VOLUME_PER_SOUND_DESCRIPTOR = msg({
	message: 'Volume per sound',
	comment: 'Phone Settings > Notifications > Message sounds: row that opens the per-sound volume sliders.',
});
const PREVIEW_SOUND_DESCRIPTOR = msg({
	message: 'Preview {label}',
	comment: 'Tooltip and accessible name of the play button next to a notification sound. Preserve {label}.',
});
const UPLOAD_SOUND_DESCRIPTOR = msg({
	message: 'Upload a sound for {label}',
	comment: 'Tooltip and accessible name of the upload button next to a notification sound. Preserve {label}.',
});
const REMOVE_SOUND_DESCRIPTOR = msg({
	message: 'Remove your sound for {label}',
	comment: 'Tooltip and accessible name of the remove button next to a custom notification sound. Preserve {label}.',
});

type SoundsProps = React.ComponentProps<typeof Sounds>;

const DEFAULT_MASTER = 100;
const MESSAGE_SOUNDS: ReadonlyArray<{type: SoundType; icon: React.ReactNode}> = [
	{type: SoundTypes.Message, icon: <HashIcon size={20} />},
	{type: SoundTypes.DirectMessage, icon: <ChatCircleIcon size={20} />},
	{type: SoundTypes.SameChannelMessage, icon: <ChatCircleDotsIcon size={20} />},
];
const MESSAGE_SOUND_TYPES = new Set<SoundType>(MESSAGE_SOUNDS.map((sound) => sound.type));

type Panel = 'calls' | 'custom' | 'volume' | null;

/** The master volume card, shown under the Sounds heading. */
export const PhoneSoundLevels: React.FC<
	Pick<SoundsProps, 'soundSettings' | 'onMasterVolumeChange' | 'onToggleAllSounds'>
> = observer(({soundSettings, onMasterVolumeChange, onToggleAllSounds}) => {
	const {i18n} = useLingui();
	const muteLabelId = useId();
	const masterVolume = soundSettings.masterVolume ?? DEFAULT_MASTER;
	const allDisabled = soundSettings.allSoundsDisabled;
	return (
		<div className={styles.card} data-flx="user.notifications-tab.phone-sounds.levels">
			<div className={styles.row}>
				<span className={styles.tile} aria-hidden="true">
					<SpeakerHighIcon size={20} />
				</span>
				<span className={styles.volume}>
					<Slider
						value={masterVolume}
						defaultValue={masterVolume}
						factoryDefaultValue={DEFAULT_MASTER}
						minValue={0}
						maxValue={200}
						step={1}
						disabled={allDisabled}
						ariaLabel={i18n._(VOLUME_DESCRIPTOR)}
						onValueRender={formatRoundedPercentage}
						onValueChange={onMasterVolumeChange}
						data-flx="user.notifications-tab.phone-sounds.master-slider"
					/>
					<span className={styles.volumeValue} aria-hidden="true">
						{formatRoundedPercentage(masterVolume)}
					</span>
				</span>
			</div>
			<div className={styles.row}>
				<span className={styles.tile} aria-hidden="true">
					<SpeakerSlashIcon size={20} />
				</span>
				<span className={styles.text}>
					<span id={muteLabelId} className={styles.title}>
						{i18n._(MUTE_ALL_SOUNDS_DESCRIPTOR)}
					</span>
					<span className={styles.description}>{i18n._(KEEPS_YOUR_CHOICES_DESCRIPTOR)}</span>
				</span>
				<Switch
					compact
					value={allDisabled}
					onChange={onToggleAllSounds}
					ariaLabelledBy={muteLabelId}
					data-flx="user.notifications-tab.phone-sounds.mute-all"
				/>
			</div>
		</div>
	);
});

/** Message sounds, then the calls/voice, custom sound and per-sound volume disclosures. */
export const PhoneMessageSounds: React.FC<SoundsProps> = observer((props) => {
	const {soundSettings, soundTypeLabels, customSounds, isSoundEnabled, onToggleSound, onPreviewSound} = props;
	const {onUploadClick, onCustomSoundDelete} = props;
	const {i18n} = useLingui();
	const [open, setOpen] = useState<Panel>(null);
	const allDisabled = soundSettings.allSoundsDisabled;
	const shortLabels: Partial<Record<SoundType, string>> = {
		[SoundTypes.Message]: i18n._(SERVER_MESSAGES_DESCRIPTOR),
		[SoundTypes.DirectMessage]: i18n._(DIRECT_MESSAGES_DESCRIPTOR),
		[SoundTypes.SameChannelMessage]: i18n._(CHAT_YOURE_IN_DESCRIPTOR),
	};
	const otherSounds = (Object.keys(soundTypeLabels) as Array<SoundType>).filter(
		(type) => !MESSAGE_SOUND_TYPES.has(type),
	);
	const allSounds = Object.keys(soundTypeLabels) as Array<SoundType>;
	const toggle = (panel: Exclude<Panel, null>) => setOpen((current) => (current === panel ? null : panel));
	const customBadge = (type: SoundType) =>
		customSounds[type] ? (
			<span className={styles.custom}>
				<Trans>Custom</Trans>
			</span>
		) : null;
	const soundRow = (type: SoundType, icon: React.ReactNode, label: string) => (
		<div key={type} className={styles.row} data-flx="user.notifications-tab.phone-sounds.sound">
			<span className={styles.tile} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.text}>
				<span className={styles.title}>
					{label}
					{customBadge(type)}
				</span>
			</span>
			<Tooltip text={i18n._(PREVIEW_SOUND_DESCRIPTOR, {label})}>
				<button
					type="button"
					className={clsx(styles.iconButton, styles.play)}
					onClick={() => onPreviewSound(type)}
					disabled={allDisabled}
					aria-label={i18n._(PREVIEW_SOUND_DESCRIPTOR, {label})}
					data-flx="user.notifications-tab.phone-sounds.preview"
				>
					<PlayIcon size={15} />
				</button>
			</Tooltip>
			<Switch
				compact
				value={allDisabled ? false : isSoundEnabled(type)}
				disabled={allDisabled}
				onChange={(enabled) => onToggleSound(type, enabled)}
				ariaLabel={label}
				data-flx="user.notifications-tab.phone-sounds.toggle"
			/>
		</div>
	);
	const disclosure = (panel: Exclude<Panel, null>, icon: React.ReactNode, label: string, meta?: string) => (
		<button
			type="button"
			className={styles.disclosure}
			aria-expanded={open === panel}
			onClick={() => toggle(panel)}
			data-flx={`user.notifications-tab.phone-sounds.disclosure.${panel}`}
		>
			<span className={styles.disclosureIcon} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.disclosureLabel}>{label}</span>
			{meta ? <span className={styles.disclosureMeta}>{meta}</span> : null}
			<CaretRightIcon size={16} className={clsx(styles.chevron, open === panel && styles.chevronOpen)} />
		</button>
	);
	return (
		<>
			<h4 className={styles.subLabel}>{i18n._(MESSAGE_SOUNDS_DESCRIPTOR)}</h4>
			<div className={styles.card} data-flx="user.notifications-tab.phone-sounds.messages">
				{MESSAGE_SOUNDS.map(({type, icon}) => soundRow(type, icon, shortLabels[type] ?? soundTypeLabels[type]))}
				{disclosure('calls', <HeadsetIcon size={20} />, i18n._(CALLS_AND_VOICE_DESCRIPTOR), String(otherSounds.length))}
				{open === 'calls' && (
					<div className={styles.nested}>
						{otherSounds.map((type) => soundRow(type, getSoundIcon(type), soundTypeLabels[type]))}
					</div>
				)}
				{disclosure(
					'custom',
					<UploadIcon size={20} />,
					i18n._(YOUR_OWN_SOUNDS_DESCRIPTOR),
					i18n._(UP_TO_SIZE_DESCRIPTOR, {size: CUSTOM_SOUND_MAX_SIZE_LABEL}),
				)}
				{open === 'custom' && (
					<div className={styles.nested}>
						<p className={styles.note}>{i18n._(PICK_A_SOUND_DESCRIPTOR)}</p>
						{allSounds.map((type) => {
							const label = shortLabels[type] ?? soundTypeLabels[type];
							return (
								<div key={type} className={styles.row} data-flx="user.notifications-tab.phone-sounds.custom-sound">
									<span className={styles.tile} aria-hidden="true">
										{getSoundIcon(type)}
									</span>
									<span className={styles.text}>
										<span className={styles.title}>
											{label}
											{customBadge(type)}
										</span>
									</span>
									{customSounds[type] && (
										<Tooltip text={i18n._(REMOVE_SOUND_DESCRIPTOR, {label})}>
											<button
												type="button"
												className={styles.iconButton}
												onClick={() => onCustomSoundDelete(type)}
												aria-label={i18n._(REMOVE_SOUND_DESCRIPTOR, {label})}
												data-flx="user.notifications-tab.phone-sounds.custom-remove"
											>
												<TrashIcon size={17} />
											</button>
										</Tooltip>
									)}
									<Tooltip text={i18n._(UPLOAD_SOUND_DESCRIPTOR, {label})}>
										<button
											type="button"
											className={styles.iconButton}
											onClick={() => onUploadClick(type)}
											aria-label={i18n._(UPLOAD_SOUND_DESCRIPTOR, {label})}
											data-flx="user.notifications-tab.phone-sounds.custom-upload"
										>
											<UploadIcon size={17} />
										</button>
									</Tooltip>
								</div>
							);
						})}
					</div>
				)}
				{disclosure('volume', <SlidersHorizontalIcon size={20} />, i18n._(VOLUME_PER_SOUND_DESCRIPTOR))}
				{open === 'volume' && (
					<div className={styles.overrides}>
						<SoundVolumeOverrides {...props} />
					</div>
				)}
			</div>
		</>
	);
});
